"use client";

import {
  type ChartConfig,
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
} from "@shared-components/shadcn/chart";
import { api } from "@shared-server/api";
import { Bar, BarChart, CartesianGrid, XAxis, YAxis, Legend } from "recharts";
import { formatGranularDateTick, COLORS } from "./metric-utils";
import type { GranularityType } from "@shared-server/api/routers/queries/metrics/utils";
import type { ResearchProjectTypeEnumType } from "@research-ai/_database/schema";
import { useDepartmentStackContext } from "@shared-components/department-stack-provider";
import { ResearchProjectTypeLabels } from "@research-ai/_database/enums";
import { ResearchProjectTypeValues } from "@research-ai/_database/enums";

const chartConfig: ChartConfig = ResearchProjectTypeValues.reduce(
  (acc, type, index) => {
    acc[type] = {
      label: ResearchProjectTypeLabels[type] || type,
      color: COLORS[index % COLORS.length],
    };
    return acc;
  },
  {} as ChartConfig,
);

type ResearchProjectsByTypeProps = {
  startDate: Date;
  endDate: Date;
  granularity: GranularityType;
};

export const ResearchProjectsByType = (props: ResearchProjectsByTypeProps) => {
  const { parentDepartmentId } = useDepartmentStackContext();
  // Query
  const researchMetricsQuery = api.coreMetrics.researchProjectsByType.useQuery({
    startDate: props.startDate,
    endDate: props.endDate,
    granularity: props.granularity,
    parentDepartmentId: parentDepartmentId,
  });

  // Chart data
  const chartData =
    researchMetricsQuery.data?.chartData.map((item) => ({
      date: item.date,
      ...item.typeData,
    })) ?? [];

  // Type totals & defaulting to empty
  const typeTotals: Record<ResearchProjectTypeEnumType, number> =
    researchMetricsQuery.data?.typeTotals ??
    ({} as Record<ResearchProjectTypeEnumType, number>);
  const totalProjects = Object.values(typeTotals).reduce(
    (acc, val) => acc + val,
    0,
  );

  return (
    <div>
      <h2 className="mb-4">{"Research Projects by Type"}</h2>
      {chartData.length === 0 && (
        <div className="text-gray-500">
          {"No data available for selected range."}
        </div>
      )}
      {chartData.length !== 0 && (
        <>
          <p className="mb-4 text-sm text-muted-foreground">
            {"Total: "}
            {totalProjects}
            {" projects"}
            {ResearchProjectTypeValues.map((type) => (
              <span key={type} className="ml-4">
                {ResearchProjectTypeLabels[type] || type}:{" "}
                {typeTotals[type] ?? 0}
              </span>
            ))}
          </p>
          <ChartContainer
            config={chartConfig}
            className="aspect-auto h-[200px] w-full"
          >
            <BarChart accessibilityLayer data={chartData}>
              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                axisLine={false}
                tickMargin={8}
                minTickGap={32}
                tickFormatter={(value: string) =>
                  formatGranularDateTick(value, props.granularity)
                }
              />
              <YAxis orientation="left" />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    nameKey="date"
                    labelFormatter={(value: string) => value}
                  />
                }
              />
              <Legend />
              {ResearchProjectTypeValues.map((type, index) => (
                <Bar
                  key={type}
                  dataKey={type}
                  stackId="research"
                  fill={COLORS[index % COLORS.length]}
                  name={ResearchProjectTypeLabels[type] || type}
                />
              ))}
            </BarChart>
          </ChartContainer>
        </>
      )}
    </div>
  );
};
